import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

/**
 * PhoneNumberInput
 *
 * Supports two usage patterns:
 *
 * 1. Controlled (JourneyPage / builder pattern):
 *    <PhoneNumberInput value={{ phone_number: "9876543210" }} onChange={fn} />
 *
 * 2. Legacy (direct store pattern via initialData):
 *    <PhoneNumberInput initialData={{ phone_number: "9876543210" }} onDataChange={fn} />
 *
 * The `value` / `onChange` pattern takes precedence when provided.
 */
interface PhoneNumberInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { phone_number?: string | number };
  onChange?: (val: { phone_number: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;

  // Legacy pattern (kept for backward compatibility)
  initialData?: { phone_number?: string | number };
  onDataChange?: (data: { phone_number: string }) => void;
}

function validatePhone(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'Phone number is required';
  if (!/^\d+$/.test(trimmed)) return 'Phone number must contain digits only';
  if (trimmed.length !== 10) return 'Phone number must be exactly 10 digits';
  if (/^[0-5]/.test(trimmed)) return 'Phone number must start with 6, 7, 8, or 9';
  return '';
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  initialData,
  onDataChange,
}) => {
  const rawPhone = value?.phone_number ?? initialData?.phone_number ?? '';
  const phoneNumber = String(rawPhone === null || rawPhone === undefined ? '' : rawPhone);

  const [touched, setTouched] = useState(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched(true);
      const raw = valueRef.current?.phone_number ?? '';
      const phone = String(raw === null || raw === undefined ? '' : raw);
      return validatePhone(phone) === '';
    });
  }, [registerSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanValue = e.target.value.replace(/\D/g, '').slice(0, 10);
    if (onChange) onChange({ phone_number: cleanValue });
    onDataChange?.({ phone_number: cleanValue });
  }, [onChange, onDataChange]);

  const error = touched ? validatePhone(phoneNumber) : '';

  return (
    <div className="space-y-1.5">
      <Label htmlFor="phone_number" className="text-gray-500 font-medium">
        Phone Number <span className="text-red-500">*</span>
      </Label>
      <Input
        id="phone_number"
        type="tel"
        value={phoneNumber}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="Enter 10-digit phone number"
        disabled={disabled}
        maxLength={10}
        inputMode="numeric"
        className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 ${error ? 'border-red-400 focus:border-red-500' : ''}`}
      />
      {error && (
        <p className="text-xs text-red-500 mt-0.5">{error}</p>
      )}
    </div>
  );
};

export default PhoneNumberInput;
